import { get } from '@/services/http';
import { AutomationConfigModel } from '@/models/automation_config';

/**
 * Manages a collection of `AutomationConfigModel` instances.
 * This class provides methods for fetching, paginating, and performing
 * basic CRUD operations on a list of automation configurations.
 *
 * Note: All fetched configurations are automatically filtered by the backend
 * to exclude soft-deleted configurations (isDeleted = true). This collection
 * will only contain active, non-deleted configurations.
 */
export class AutomationConfigList {
  /** The array of AutomationConfigModel instances in the current page. */
  public items: AutomationConfigModel[] = [];

  /** The total number of configurations available on the server. */
  public total: number = 0;

  /** The current page number for pagination. */
  public page: number = 1;

  /** The number of items per page for pagination. */
  public limit: number = 20;

  /**
   * Constructs an instance of AutomationConfigList.
   */
  constructor() {
    this.items = [];
    this.total = 0;
  }

  /**
   * Fetches a paginated and filtered list of automation configurations from the server.
   *
   * Note: The backend automatically filters out soft-deleted configurations,
   * so only active configurations will be returned in the response.
   *
   * @param {object} [params={}] - The query parameters for filtering and pagination.
   * @param {number} [params.page] - The page number to fetch.
   * @param {number} [params.limit] - The number of items per page.
   * @param {string} [params.sort_by] - The field to sort by.
   * @param {string} [params.sort_order] - The sort direction ('ASC' or 'DESC').
   * @param {string} [params.q] - A search query string.
   * @param {boolean} [params.is_active] - Filter by active status.
   * @param {number} [params.supplier_id] - Filter by supplier ID.
   * @returns {Promise<void>}
   */
  public async fetch(params: any = {}): Promise<void> {
    try {
      const response: any = await get('/po-automation/configs', params);

      if (response && response.data) {
        this.items = [];
        this.total = response.data.pagination?.total || response.data.items.length;
        this.page = params.page || 1;
        this.limit = params.limit || 20;

        for (const item of response.data.items) {
          const config = new AutomationConfigModel(item);
          this.items.push(config);
        }
      }
    } catch (error) {
      console.error('Error fetching automation configs:', error);
      throw error;
    }
  }

  /**
   * Adds a configuration to the local collection.
   * @param {AutomationConfigModel} config - The configuration model to add.
   */
  public add(config: AutomationConfigModel): void {
    this.items.push(config);
    this.total++;
  }

  /**
   * Removes a configuration from the local collection by its ID.
   *
   * Note: This only removes the item from the local collection in memory.
   * To perform a soft delete on the server, use the configuration's delete() method.
   *
   * @param {number} id - The ID of the configuration to remove from local collection.
   */
  public remove(id: number): void {
    const index = this.items.findIndex((item) => item.id === id);
    if (index !== -1) {
      this.items.splice(index, 1);
      this.total--;
    }
  }

  /**
   * Updates a configuration in the local collection.
   *
   * Note: This only updates the item in the local collection in memory.
   * To save changes to the server, use the configuration's save() method.
   *
   * @param {number} id - The ID of the configuration to update.
   * @param {Partial<AutomationConfigModel>} updates - An object with the properties to update.
   */
  public update(id: number, updates: Partial<AutomationConfigModel>): void {
    const index = this.items.findIndex((item) => item.id === id);
    if (index !== -1) {
      Object.assign(this.items[index], updates);
    }
  }

  /**
   * Finds a configuration in the local collection by its ID.
   * @param {number} id - The ID of the configuration to find.
   * @returns {AutomationConfigModel | undefined} The found configuration or undefined.
   */
  public findById(id: number): AutomationConfigModel | undefined {
    return this.items.find((item) => item.id === id);
  }

  /**
   * Returns the number of items in the current local collection.
   * @returns {number} The number of items.
   */
  public size(): number {
    return this.items.length;
  }

  /**
   * Clears all items from the local collection and resets pagination.
   */
  public clear(): void {
    this.items = [];
    this.total = 0;
  }
}
